import {
  EGameConfigs,
  IMapConfigEntry,
  IMapConfigEntryDomination,
  IMapConfigEntryOrLoose,
  IMapEntityBotLoose,
  IMapEntityLoose,
  IRoomLoose,
  ISpawnConfigLoose,
  ISpawnSetLoose
} from '@nova/types';
import { ReadonlyDeep, WritableDeep } from 'type-fest';

import { mapConfigDominationLiteral, mapConfigOrLiteral } from '../configs';

import { EntityDatasetKey } from './entity-types';

// ==============================
//  Strict Map Entity Types
// ==============================

/**
 * Map entity with type-safe entity dataset key.
 */
export interface IMapEntity extends Omit<IMapEntityLoose, 'id'> {
  id: EntityDatasetKey;
}

/**
 * Map entity bot with type-safe entity dataset key.
 */
export interface IMapEntityBot extends Omit<IMapEntityBotLoose, 'id'> {
  id: EntityDatasetKey;
}

export interface ISpawnSet extends Omit<ISpawnSetLoose, 'entities'> {
  entities: IMapEntity[];
}

export interface ISpawnConfig extends Omit<ISpawnConfigLoose, 'spawnSets'> {
  spawnSets: ISpawnSet[];
}

export interface IRoom extends Omit<IRoomLoose, 'initEntities' | 'initBots'> {
  initEntities: IMapEntity[];
  initBots?: IMapEntityBot[];
}

export type RoomSet = { [roomIndex: string]: IRoom | undefined };

export interface IMapConfigEntryOr extends Omit<IMapConfigEntryOrLoose, 'rooms'> {
  rooms: RoomSet;
}

export function isMapConfigEntryOr(mapConfig: IMapConfigEntry): mapConfig is IMapConfigEntryOr {
  return mapConfig.type === 'trenchRun'
    || 'rooms' in mapConfig;
}

// ==============================
//  Map Config Literals
// ==============================

mapConfigOrLiteral satisfies ReadonlyDeep<IMapConfigEntry[]>;
type MapConfigOrLiteral = typeof mapConfigOrLiteral;
export type MapConfigOr = WritableDeep<MapConfigOrLiteral>;

mapConfigDominationLiteral satisfies ReadonlyDeep<IMapConfigEntryDomination[]>;
type MapConfigDominationLiteral = typeof mapConfigDominationLiteral;
export type MapConfigDomination = WritableDeep<MapConfigDominationLiteral>;

export type MapConfigTypeToConfigKeyMap = {
  [EGameConfigs.MAPS_OR]: MapConfigOr;
  [EGameConfigs.MAPS_DOMINATION]: MapConfigDomination;
};
